import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from '../icons';
import { Popover } from './popover';

/**
 * The styled select (refonte: "no native `<select>`").
 *
 * A select-only combobox in the WAI-ARIA sense: focus stays on the trigger
 * button, the listbox opens in a popover, and `aria-activedescendant` names the
 * option the arrow keys are on. Enter/Space pick it, Escape closes, letters
 * jump to the next option starting with them.
 *
 * It keeps the native element's API on purpose — `value`, `onChange(event)`
 * reading `event.target.value`, and `<option>` / `<optgroup>` children — so
 * every existing call site switched over without being rewritten. New code can
 * pass `options` instead of children. A disabled option with an empty value is
 * the placeholder, as with a native select, and is not listed.
 */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

/** The slice of a change event the call sites read. */
export interface SelectChangeEvent {
  target: { value: string };
}

/** `icon`: the icon alone in a square, named by `aria-label`, which must then say the current value (a phone header). */
export type SelectVariant = 'field' | 'pill' | 'filter' | 'bare' | 'icon';

export interface SelectProps {
  value: string;
  onChange?: (event: SelectChangeEvent) => void;
  options?: SelectOption[];
  children?: ReactNode;
  placeholder?: string;
  /** Text shown in the trigger instead of the chosen option's label. */
  displayLabel?: string;
  variant?: SelectVariant;
  icon?: IconName;
  /** Shown before the value in place of `icon`, for what is not a UI icon (a channel's logo). */
  leading?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  className?: string;
  title?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

interface OptionElementProps {
  value?: string | number;
  disabled?: boolean;
  label?: string;
  children?: ReactNode;
}

function parseOptions(children: ReactNode, group?: string): SelectOption[] {
  const out: SelectOption[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<OptionElementProps>(child)) return;
    if (child.type === Fragment) {
      out.push(...parseOptions(child.props.children, group));
    } else if (child.type === 'optgroup') {
      out.push(...parseOptions(child.props.children, child.props.label));
    } else if (child.type === 'option') {
      const label = textOf(child.props.children);
      const value = child.props.value === undefined ? label : String(child.props.value);
      out.push({ value, label, disabled: Boolean(child.props.disabled), group });
    }
  });
  return out;
}

const TRIGGER_STYLES: Record<SelectVariant, string> = {
  field:
    'min-h-[42px] gap-2 rounded-(--radius-control) bg-(--color-surface) px-3.5 py-[9px] text-[13.5px] ring-1 ring-(--color-line-control) hover:ring-(--color-ink-300) aria-expanded:ring-2 aria-expanded:ring-(--color-accent)',
  pill: 'h-[34px] gap-1.5 rounded-[10px] bg-(--color-surface) px-2.5 text-[12.5px] font-semibold ring-1 ring-(--color-line) hover:bg-(--color-surface-sunken) aria-expanded:ring-(--color-accent)',
  filter:
    'h-[38px] gap-1.5 rounded-[10px] border border-dashed border-(--color-ink-300) bg-(--color-surface) px-3 text-[12.5px] font-semibold text-(--color-ink-700) hover:border-(--color-accent) hover:text-(--color-ink-900)',
  bare: 'h-8 gap-1.5 rounded-md px-1 text-[12.5px] font-semibold',
  icon: 'size-[34px] justify-center rounded-[10px] bg-(--color-surface) ring-1 ring-(--color-line) hover:bg-(--color-surface-sunken) aria-expanded:ring-(--color-accent)',
};

/** The list under an icon trigger: wide enough for names, on the trigger's right edge. */
const ICON_LIST_WIDTH = 220;

const isPlaceholder = (o: SelectOption) => o.value === '' && o.disabled;

export function Select({
  value,
  onChange,
  options: optionsProp,
  children,
  placeholder: placeholderProp,
  displayLabel,
  variant = 'field',
  icon,
  leading,
  disabled = false,
  required,
  name,
  id,
  className = '',
  title,
  ...aria
}: SelectProps) {
  const baseId = useId();
  const listId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const parsed = useMemo(() => optionsProp ?? parseOptions(children), [optionsProp, children]);
  const listed = useMemo(() => parsed.filter((o) => !isPlaceholder(o)), [parsed]);
  const placeholder = placeholderProp ?? parsed.find(isPlaceholder)?.label;
  const selectedIndex = listed.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? listed[selectedIndex] : undefined;
  // Like a native select: an unmatched value without a placeholder shows the first option.
  const shown = displayLabel ?? selected?.label ?? placeholder ?? listed[0]?.label ?? '';
  const muted = !displayLabel && !selected && Boolean(placeholder);

  const enabledIndexes = listed.flatMap((o, i) => (o.disabled ? [] : [i]));

  // Consecutive options of one <optgroup> render as one labelled group.
  const sections = useMemo(() => {
    const out: { group?: string; items: { option: SelectOption; index: number }[] }[] = [];
    listed.forEach((option, index) => {
      const last = out[out.length - 1];
      if (last && last.group === option.group) last.items.push({ option, index });
      else out.push({ group: option.group, items: [{ option, index }] });
    });
    return out;
  }, [listed]);

  const openList = (at?: number) => {
    if (disabled) return;
    setActive(at ?? (selectedIndex >= 0 ? selectedIndex : (enabledIndexes[0] ?? -1)));
    setOpen(true);
  };
  const close = () => setOpen(false);
  const commit = (index: number) => {
    const option = listed[index];
    if (!option || option.disabled) return;
    if (option.value !== value) onChange?.({ target: { value: option.value } });
    close();
  };

  const step = (direction: 1 | -1) => {
    if (enabledIndexes.length === 0) return;
    const position = enabledIndexes.indexOf(active);
    const next = position === -1 ? 0 : (position + direction + enabledIndexes.length) % enabledIndexes.length;
    setActive(enabledIndexes[next]!);
  };

  const typeAhead = (key: string) => {
    const lower = key.toLowerCase();
    const start = enabledIndexes.indexOf(active);
    const ordered = [...enabledIndexes.slice(start + 1), ...enabledIndexes.slice(0, start + 1)];
    const match = ordered.find((i) => listed[i]!.label.toLowerCase().startsWith(lower));
    if (match !== undefined) {
      if (open) setActive(match);
      else openList(match);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    // Ctrl+F, ⌘K, Ctrl+R… belong to the browser and the app, not to the typeahead.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openList();
      } else if (event.key.length === 1 && /\S/.test(event.key)) {
        typeAhead(event.key);
      }
      return;
    }
    if (event.key === 'ArrowDown') step(1);
    else if (event.key === 'ArrowUp') step(-1);
    else if (event.key === 'Home') setActive(enabledIndexes[0] ?? -1);
    else if (event.key === 'End') setActive(enabledIndexes[enabledIndexes.length - 1] ?? -1);
    else if (event.key === 'Enter' || event.key === ' ') commit(active);
    else if (event.key === 'Tab') {
      close();
      return;
    } else if (event.key.length === 1 && /\S/.test(event.key)) {
      typeAhead(event.key);
    } else return;
    event.preventDefault();
  };

  // Keep the active option in view while the arrow keys walk a long list.
  useEffect(() => {
    if (open && active >= 0) document.getElementById(`${baseId}-option-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active, baseId]);

  const isIcon = variant === 'icon';
  const width = isIcon || /(^|\s)w-/.test(className) ? '' : 'w-full';
  // `filter` and `bare` take their colour from the pill around them.
  const toneClass = variant === 'filter' || variant === 'bare' ? '' : 'text-(--color-ink-900)';

  return (
    <span className={`relative inline-flex min-w-0 ${width} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        title={title}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-label={aria['aria-label']}
        aria-labelledby={aria['aria-labelledby']}
        aria-describedby={aria['aria-describedby']}
        aria-required={required || undefined}
        disabled={disabled}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
        className={`touch-target flex ${isIcon ? '' : 'w-full'} min-w-0 items-center text-left whitespace-nowrap transition-[box-shadow,background-color,border-color,color] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent) disabled:cursor-not-allowed disabled:opacity-60 ${toneClass} ${TRIGGER_STYLES[variant]}`}
      >
        {leading ?? (icon ? <Icon name={icon} className={`shrink-0 ${isIcon ? 'size-[17px] text-(--color-ink-700)' : 'size-[15px] text-(--color-ink-500)'}`} /> : null)}
        {isIcon ? null : <span className={`min-w-0 flex-1 truncate ${muted ? 'text-(--color-ink-500)' : ''}`}>{shown}</span>}
        {variant === 'filter' || isIcon ? null : (
          <Icon name="chevronDown" className="size-4 shrink-0 text-(--color-ink-500)" />
        )}
      </button>
      {/* Native constraint validation and form posts still see a value. */}
      {required || name ? (
        <input
          tabIndex={-1}
          aria-hidden
          required={required}
          name={name}
          value={value}
          onChange={() => undefined}
          className="pointer-events-none absolute inset-0 -z-10 opacity-0"
        />
      ) : null}
      <Popover
        anchorRef={triggerRef}
        open={open}
        onClose={close}
        width={isIcon ? ICON_LIST_WIDTH : undefined}
        align={isIcon ? 'end' : 'start'}
        className="max-w-[min(360px,calc(100vw-16px))]"
      >
        <div role="listbox" id={listId} aria-label={aria['aria-label']}>
          {sections.map((section, sectionIndex) => {
            const rows = section.items.map(({ option, index }) => {
              const isSelected = index === selectedIndex;
              return (
                <div
                  key={`${option.value}|${index}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  title={option.label}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => !option.disabled && setActive(index)}
                  onClick={() => commit(index)}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] ${
                    index === active ? 'bg-(--color-accent-wash-soft) text-(--color-ink-900)' : 'text-(--color-ink-700)'
                  } ${isSelected ? 'font-semibold text-(--color-ink-900)' : ''} ${
                    option.disabled ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {isSelected ? <Icon name="check" className="size-4 shrink-0 text-(--color-accent)" /> : null}
                </div>
              );
            });
            if (!section.group) return <Fragment key={`section-${sectionIndex}`}>{rows}</Fragment>;
            return (
              <div key={`section-${sectionIndex}`} role="group" aria-label={section.group}>
                <div aria-hidden className="px-2.5 pb-1 pt-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-(--color-ink-500)">
                  {section.group}
                </div>
                {rows}
              </div>
            );
          })}
        </div>
      </Popover>
    </span>
  );
}
